import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Button } from "./ui/button";
import { Share2, Check, ExternalLink, Copy, HelpCircle } from "lucide-react";

/**
 * "Share to Denzel FB" button for a published post that has a Facebook permalink.
 *
 * Flow:
 *   1. Copies the follow snippet ("Great stuff from {brand} — go follow them...") to
 *      the clipboard.
 *   2. Opens facebook.com/sharer/sharer.php popup with the post permalink so FB auto-loads
 *      the full preview.
 *   3. Detects the user returning to this tab and shows a "Did you post it?" confirm
 *      prompt — only marks the post shared once the user confirms (no blind marking).
 */
export default function DenzelShareButton({ post, size = "sm", variant = "outline",
                                            onShared, className = "" }) {
  const [busy, setBusy] = useState(false);
  const [awaitingReturn, setAwaitingReturn] = useState(false);
  const permalink = post?.permalink_url;
  const snippet = post?.follow_snippet || "";
  const followFb = post?.follow_urls?.facebook;
  const followIg = post?.follow_urls?.instagram;
  const alreadyShared = post?.denzel_share_status === "shared";

  useEffect(() => {
    if (!awaitingReturn) return;
    const onVis = () => {
      if (document.visibilityState === "visible" && awaitingReturn) {
        setTimeout(() => toast.info(`Welcome back — did you post "${post.title}" to Facebook?`), 200);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [awaitingReturn, post?.title]);

  if (!permalink) return null;

  const openShare = async () => {
    setBusy(true);
    try {
      if (snippet) {
        try { await navigator.clipboard.writeText(snippet); } catch (_e) { /* ignore */ }
      }
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(permalink)}`;
      window.open(url, "_blank", "noopener,noreferrer,width=680,height=700");
      toast.success(
        snippet
          ? "Snippet copied — paste it on FB, then come back to confirm"
          : "FB share dialog opened — come back to confirm",
        { duration: 4500 }
      );
      setAwaitingReturn(true);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmShared = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/denzel/share-tray/${post.id}/mark-shared`);
      setAwaitingReturn(false);
      onShared?.(data);
      toast.success("Marked as shared to Denzel", {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/denzel/share-tray/${post.id}/mark-pending`);
              onShared?.({ ...data, denzel_share_status: "pending" });
              toast.message("Reverted — post moved back to pending");
            } catch (e) { toast.error(errText(e)); }
          },
        },
      });
    } catch (e) {
      toast.error(errText(e));
    } finally { setBusy(false); }
  };

  const notYet = () => setAwaitingReturn(false);

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Follow snippet copied");
    } catch (e) { toast.error("Copy failed"); }
  };

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {alreadyShared ? (
        <Button size={size} variant="ghost" disabled data-testid={`denzel-shared-badge-${post.id}`}>
          <Check size={13} className="mr-1" /> Shared to Denzel
        </Button>
      ) : awaitingReturn ? (
        <>
          <span className="text-[11px] text-accent inline-flex items-center gap-1 mr-1"
                data-testid={`denzel-return-prompt-${post.id}`}>
            <HelpCircle size={11} /> Did you post it?
          </span>
          <Button size={size} variant="default" disabled={busy} onClick={confirmShared}
                  data-testid={`denzel-confirm-shared-${post.id}`}>
            <Share2 size={13} className="mr-1" /> Yes, mark shared
          </Button>
          <Button size={size} variant="ghost" disabled={busy} onClick={notYet}
                  data-testid={`denzel-notyet-${post.id}`}>
            Not yet
          </Button>
        </>
      ) : (
        <Button size={size} variant={variant} disabled={busy}
                onClick={openShare} data-testid={`denzel-share-btn-${post.id}`}
                title="Copies a follow snippet and opens Facebook's share dialog. Come back to confirm.">
          <Share2 size={13} className="mr-1" />
          Share to Denzel FB
        </Button>
      )}
      {!awaitingReturn && !alreadyShared && snippet && (
        <Button size={size} variant="ghost" onClick={copySnippet}
                data-testid={`denzel-copy-snippet-${post.id}`}
                title="Copy follow snippet to clipboard">
          <Copy size={12} />
        </Button>
      )}
      {followFb && !awaitingReturn && (
        <a href={followFb} target="_blank" rel="noopener noreferrer"
           className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
           data-testid={`denzel-follow-fb-${post.id}`}
           title={`Follow ${post.brand} on Facebook`}>
          <ExternalLink size={11} /> FB
        </a>
      )}
      {followIg && !awaitingReturn && (
        <a href={followIg} target="_blank" rel="noopener noreferrer"
           className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
           data-testid={`denzel-follow-ig-${post.id}`}
           title={`Follow ${post.brand} on Instagram`}>
          <ExternalLink size={11} /> IG
        </a>
      )}
    </div>
  );
}
